import React, { Suspense, useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import BrowserDetection from './components/BrowserDetection';
// Firebase import removed - using Worker instead
// import { auth } from './firebase';
import GDPRConsent from './components/GDPRConsent';
import Clarity from '@microsoft/clarity';
import useAdminAuth from './hooks/useAdminAuth';
import YektanetScript from './components/YektanetScript';
import GoogleAnalytics from './components/GoogleAnalytics';
import AppRoutes from './AppRoutes';
import ProtectedRoute from './ProtectedRoute';
import InstallButton from './components/InstallButton';
import * as serviceWorker from './utils/serviceWorker';
import './styles/ui-kit.css';
// Import notification manager (Microservice Architecture)
import notificationManager from './services/notifications';

// General Pages
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import HomePage from './pages/HomePage';
import Profile from './pages/Profile';
import Scan from './pages/Scan';
import SkinAnalysisPage from './pages/SkinAnalysisPage';
import Routine from './pages/Routine';
import RoutineRequestCheck from './pages/RoutineRequestCheck';
import ProfitScanPage from './pages/ProfitScanPage';
import TestKYC from './pages/testkyc';
import ProfilePage from './pages/ProfilePage';
import Privacy from './pages/Privacy';
import SettingsPage from './pages/SettingsPage';
import TestRoutine from './TestRoutine';

// Layouts
import MainLayout from './components/MainLayout';

// Admin Pages - Lazy Load
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const AdminOverview = React.lazy(() => import('./pages/admin/AdminOverview'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminAnalyses = React.lazy(() => import('./pages/admin/AdminAnalyses'));
const AdminRequests = React.lazy(() => import('./pages/admin/AdminRequests'));
const AdminAnalytics = React.lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminRoutines = React.lazy(() => import('./pages/admin/AdminRoutines'));
const AdminProductAnalyses = React.lazy(() => import('./pages/admin/AdminProductAnalyses'));
// Lazy load blog components
const AdminBlogPage = React.lazy(() => import('./pages/admin/AdminBlogPage'));
const AdminBlogPostsList = React.lazy(() => import('./pages/admin/blog/AdminBlogPostsList'));
const AdminCreateBlogPost = React.lazy(() => import('./pages/admin/blog/AdminCreateBlogPost'));
const AdminEditBlogPost = React.lazy(() => import('./pages/admin/blog/AdminEditBlogPost'));

// Worker URL
const WORKER_URL = 'https://empty-rain-2654.er-hooman.workers.dev';

// Clarity project ID from environment
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

// --- Restore the LoadingFallback component definition --- 
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-white">
    <div className="animate-spin h-12 w-12 border-4 border-black border-t-transparent rounded-full"></div>
  </div>
);

// Function to apply theme class to HTML element (can be moved to a utils file)
const applyTheme = (theme) => {
  const root = window.document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
  // No need to save to localStorage here, SettingsModal handles saving
};

// --- Refactored ProtectedRoute --- 
// (Remove the entire function definition for ProtectedRoute here)

// --- Refactored ProtectedAdminRoute using useAdminAuth --- 
const ProtectedAdminRoute = ({ children }) => {
    const { isAdmin, loading: adminAuthLoading, userId } = useAdminAuth();
    const navigate = useNavigate();

    // useEffect برای مدیریت ریدایرکت وقتی وضعیت ادمین یا کاربر تغییر می‌کند
    useEffect(() => {
        if (!adminAuthLoading) { // فقط زمانی که بررسی اولیه انجام شده
            if (!userId) {
                // اگر userId null باشد یعنی کاربری لاگین نکرده، useAdminAuth باید به /admin-login هدایت کرده باشد
                // این یک بررسی اضافی است در صورتی که useAdminAuth هنوز در حال پردازش اولیه باشد یا ریدایرکت نکرده باشد.
                // console.log("ProtectedAdminRoute: No user ID, should be handled by useAdminAuth or redirecting to /admin-login now.");
                // navigate('/admin-login', { replace: true });
                // در واقع، اگر userId نباشد، useAdminAuth به /admin-login میفرستد و isAdmin هم false خواهد بود.
            } else if (!isAdmin) {
                // کاربر لاگین کرده اما ادمین نیست
                // console.log("ProtectedAdminRoute: User is not admin, redirecting to /admin-login.");
                navigate('/admin-login', { replace: true });
            }
            // اگر userId وجود داشته باشد و isAdmin هم true باشد، چیزی انجام نمی‌دهیم و children رندر می‌شود.
        }
    }, [isAdmin, adminAuthLoading, userId, navigate]);

    if (adminAuthLoading) {
      return <LoadingFallback />; 
    }

    // اگر کاربر ادمین است، children را نمایش بده
    // اگر ادمین نیست یا در حال بارگذاری است، ریدایرکت یا LoadingFallback در بالا انجام شده است.
    return isAdmin ? children : null; 
    // اگر null برگردانده شود و useAdminAuth یا useEffect بالا ریدایرکت نکرده باشند، ممکن است صفحه سفید بماند.
    // بنابراین، اطمینان از ریدایرکت در useEffect بالا مهم است.
    // اگر isAdmin false باشد و adminAuthLoading هم false، useEffect بالا باید به /admin-login هدایت کرده باشد.
    // اگر به هر دلیلی این اتفاق نیافتاده باشد، بازگشت null باعث می‌شود چیزی رندر نشود.
};

const App = () => {
  useEffect(() => {
    // Register Service Worker
    serviceWorker.register({
      onSuccess: async (registration) => {
        console.log('Service Worker registered successfully');
        
        // Check if this is the first time the app is loaded AND notification permission
        const isFirstVisit = !localStorage.getItem('dermowise_visited');
        const hasNotificationPermission = 'Notification' in window && Notification.permission === 'granted';
        
        if (isFirstVisit) {
          console.log('🎉 First visit detected');
          
          // Request permission if not already granted
          if (!hasNotificationPermission && 'Notification' in window) {
            console.log('📱 Requesting notification permission...');
            try {
              const permission = await Notification.requestPermission();
              console.log('📱 Permission result:', permission);
              
              if (permission === 'granted') {
                // Permission granted, schedule welcome notification
                await scheduleWelcome();
              } else {
                console.log('⚠️ Notification permission not granted, skipping welcome notification');
                localStorage.setItem('dermowise_visited', 'true');
              }
            } catch (error) {
              console.error('❌ Failed to request permission:', error);
              localStorage.setItem('dermowise_visited', 'true');
            }
          } else if (hasNotificationPermission) {
            // Permission already granted, schedule welcome notification
            await scheduleWelcome();
          } else {
            // Notifications not supported
            console.log('⚠️ Notifications not supported');
            localStorage.setItem('dermowise_visited', 'true');
          }
        }
        
        async function scheduleWelcome() {
          try {
            localStorage.setItem('dermowise_visited', 'true');
            
            // Get user name and ID from localStorage
            const userProfile = localStorage.getItem('userProfile');
            let userName = 'کاربر عزیز';
            let userId = null;
            
            if (userProfile) {
              try {
                const profile = JSON.parse(userProfile);
                userName = profile.name || profile.displayName || 'کاربر عزیز';
                userId = profile.uid || profile.id || null;
              } catch (e) {
                console.log('Could not parse user profile');
              }
            }
            
            // Use new Microservice Architecture for notifications
            console.log('🎉 Setting up notifications for new user using Microservices...');
            
            const result = await notificationManager.setupForNewUser(userName, userId, {
              welcomeDelay: 10000, // 10 seconds
              subscribeToPush: true, // Subscribe to push notifications
              analysisReminderDelay: 7 * 24 * 60 * 60 * 1000, // 7 days
            });
            
            console.log('✅ Notification setup result:', result);
            
            if (result.welcome) {
              console.log('✅ Welcome notification scheduled for 10 seconds');
            }
            if (result.subscription) {
              console.log('✅ Subscribed to push notifications');
            }
          } catch (error) {
            console.error('❌ Failed to setup notifications:', error);
          }
        }
      },
      onUpdate: (registration) => {
        console.log('New content available, please refresh');
        // You can show a notification to user here
      }
    });

    // Check for old routes and redirect
    if (window.location.pathname.includes('/skin-analysis/')) {
      const newPath = window.location.pathname.replace('/skin-analysis/', '/analysis/');
      console.log('🔄 Redirecting from old route:', window.location.pathname, 'to:', newPath);
      window.history.replaceState(null, '', newPath);
    }
    
    // Register PWA install prompt
    serviceWorker.registerInstallPrompt();
    
    // Register offline detection
    serviceWorker.registerOfflineDetection();
    
    // Register background sync
    serviceWorker.registerBackgroundSync();

    // Debug navigation and force redirect
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      console.log('🚀 Navigation pushState:', args[2]);
      if (args[2] && args[2].includes('/skin-analysis/')) {
        console.error('❌ ERROR: Still using old route /skin-analysis/');
        console.trace('Route error stack trace');
        // Force redirect to new route
        const newUrl = args[2].replace('/skin-analysis/', '/analysis/');
        console.log('🔄 Force redirecting to:', newUrl);
        args[2] = newUrl;
      }
      return originalPushState.apply(this, args);
    };
    
    window.history.replaceState = function(...args) {
      console.log('🔄 Navigation replaceState:', args[2]);
      if (args[2] && args[2].includes('/skin-analysis/')) {
        console.error('❌ ERROR: Still using old route /skin-analysis/');
        console.trace('Route error stack trace');
        // Force redirect to new route
        const newUrl = args[2].replace('/skin-analysis/', '/analysis/');
        console.log('🔄 Force redirecting to:', newUrl);
        args[2] = newUrl;
      }
      return originalReplaceState.apply(this, args);
    };
  }, []);

  return (
    <div className="app-container">
      <AuthProvider>
        <Router>
          <Suspense fallback={<LoadingFallback />}>
            <BrowserDetection />
            <GoogleAnalytics />
            <GDPRConsent />
            <AppRoutes />
            <InstallButton />
          </Suspense>
        </Router>
      </AuthProvider>
    </div>
  );
};

export default App; 